import { MapPin, Phone } from 'lucide-react';
import {
    useProfileContactInfo,
    useRevealPersonalEmail,
    useRevealPhoneNumber,
    useRevealWorkEmail,
} from '../../../../api/useProfileContactInfo';
import { useAiSearchProfilePopoutContext } from '../../AiSearchProfilePopoutContext';
import HiddenProfileData from './HiddenProfileData';
import LinkedInCompanyIcon from '@/Components/TalentMeshIcons/LinkedInCompanyIcon';
import WindowsMailIcon from '@/Components/TalentMeshIcons/WindowsMailIcon';
import UIStrings from '@/Utils/UIStrings';

const ProfileSummary = () => {
    const { selectedProfile } = useAiSearchProfilePopoutContext();
    const { data } = useProfileContactInfo(selectedProfile?.url);
    const showPersonalEmail = !!data?.personalEmailAvailable;
    const showPhoneNumber = !!data?.phoneNumberAvailable;
    const showWorkEmail = !!data?.workEmailAvailable;
    const {
        data: personalEmailData,
        isLoading: isPersonalEmailLoading,
        mutate: revealPersonalEmail,
    } = useRevealPersonalEmail(selectedProfile?.url);
    const {
        data: phoneNumberData,
        isLoading: isPhoneNumberLoading,
        mutate: revealPhoneNumber,
    } = useRevealPhoneNumber(selectedProfile?.url);
    const {
        data: workEmailData,
        isLoading: isWorkEmailLoading,
        mutate: revealWorkEmail,
    } = useRevealWorkEmail(selectedProfile?.url);

    return (
        <div className="animate-in fade-in slide-in-from-bottom-1 flex flex-col gap-6 duration-300 ease-out">
            <div className="flex flex-col gap-2">
                <div className="flex flex-row flex-wrap items-center gap-2">
                    <h3 className="text-2xl font-semibold tracking-tight">{selectedProfile?.name}</h3>
                    <a href={selectedProfile?.url} target="_blank" rel="noreferrer" className="flex shrink-0">
                        <LinkedInCompanyIcon />
                    </a>
                </div>
                <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex min-w-0 flex-row items-center gap-1.5">
                        <MapPin className="mt-0.5 size-4 shrink-0" />
                        <p className="pt-1 text-sm leading-normal">
                            <span className="font-bold">{`${UIStrings.Location}: `}</span>
                            {selectedProfile?.location}
                        </p>
                    </div>
                    {showPersonalEmail && (
                        <HiddenProfileData
                            data-cy="hiddenProfileData-Personalemail"
                            isLoading={isPersonalEmailLoading}
                            label={UIStrings.PersonalEmail}
                            icon={<WindowsMailIcon />}
                            iconContainerClassName="size-6"
                            onReveal={() => revealPersonalEmail()}
                            value={personalEmailData?.personalEmails}
                        />
                    )}
                    {showWorkEmail && (
                        <HiddenProfileData
                            data-cy="hiddenProfileData-Workemail"
                            isLoading={isWorkEmailLoading}
                            label={UIStrings.WorkEmail}
                            icon={<WindowsMailIcon />}
                            iconContainerClassName="size-6"
                            onReveal={() => revealWorkEmail()}
                            value={workEmailData?.workEmails}
                        />
                    )}
                    {showPhoneNumber && (
                        <HiddenProfileData
                            data-cy="hiddenProfileData-Phonenumber"
                            isLoading={isPhoneNumberLoading}
                            label={UIStrings.PhoneNumber}
                            icon={<Phone className="fill-foreground size-4" />}
                            onReveal={() => revealPhoneNumber()}
                            value={phoneNumberData?.phoneNumbers}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfileSummary;
